<template>
	<div v-if="cluster" class="card-page career-select-wrapper">
		<ja-header
			:title="$t('gamePlayMetaTitles.careers')"
			subtitle="JA Finance Park"
		>
			<template v-slot:icon>
				<svgicon name="headerIcons/career"></svgicon>
			</template>
		</ja-header>
		<content>
			<div class="heading-image"></div>
			<h1 class="heading">{{ cluster.name }}</h1>
			<div class="career-select-card-wrapper">
				<ja-expanded-card
					v-for="(career, index) in cluster.cmsCareers"
					:key="index"
				>
					<div class="text-wrapper">
						<h2 class="heading-3">{{ career.name }}</h2>
						<span class="card-description">{{ career.description }}</span>
						<br />
						<span class="card-description">{{ career.education }}</span>

						<div v-show="isExpanded(career)" class="expanded-active">
							<div>
								<div class="heading-3">{{ $t('responsibilities') }}</div>
								<ja-list :items="career.expectedDuties"></ja-list>
							</div>

							<div class="career-path-wrapper">
								<div class="heading-3">{{ $t('careerPath') }}</div>
								<ja-icon-list-element
									v-for="(jobTitle, i) in career.cmsCareerJobTitles"
									:key="i"
									:title="jobTitle.jobTitle"
									:info="
										`Income: ${$options.filters.currency(jobTitle.salary)}`
									"
									>{{ i + 1 }}</ja-icon-list-element
								>
							</div>
						</div>
					</div>
					<div class="btn-wrapper">
						<ja-button
							class="btn-show-more small transparent"
							:name="'select' + index"
							:aria-label="
								$t(isExpanded(career) ? 'showLessCareer' : 'showMoreCareer', {
									career: career.name
								})
							"
							:aria-expanded="isExpanded(career).toString()"
							@click.native="toggleCareer(career)"
						>
							<div class="text-show-more">
								{{ isExpanded(career) ? $t(button.less) : $t(button.more) }}
							</div>
						</ja-button>
						<ja-button
							class="btn-select small outlined"
							:name="'select' + index"
							:success="careerIsSelected(career)"
							:aria-label="$t('selectCareer', { career: career.name })"
							role="switch"
							:aria-checked="careerIsSelected(career).toString()"
							@click.native="toggleCareerSelection(career)"
						>
							<div class="text-btn-select">{{ $t('select') }}</div>
						</ja-button>
					</div>
				</ja-expanded-card>
			</div>
		</content>

		<ja-alert-bar :alert-condition="alertIsVisible">
			{{ $t('careerCountAlert') }}
		</ja-alert-bar>

		<footer class="sticky-footer">
			<div class="footer-content">
				<ja-button
					name="career-back"
					class="btn-back transparent small"
					:aria-label="$t('backToClusters')"
					@click.native.once="navigateBackwards"
				>
					<svgicon class="arrow-left" name="arrow-left"></svgicon>
					<div class="text-back">{{ $t('back') }}</div>
				</ja-button>

				<div class="footer-container-right">
					<span class="counter">
						{{ $t('selectedCareerCounter', { count: selectedCareer ? 1 : 0 }) }}
					</span>
					<ja-button
						name="career-submit"
						:disabled="!selectedCareer"
						class="small transperant"
						:aria-label="$t('goEducationPage')"
						@click.native.once="submitCareer"
					>
						{{ $t('next') }}
						<svgicon name="arrow-right" class="arrow-right"></svgicon>
					</ja-button>
				</div>
			</div>
		</footer>
	</div>
</template>
<script>
import gql from 'graphql-tag';
import Button from '../components/ui/Button';
import Header from '../components/ui/headers/Header';
import ExpandedCard from '../components/ui/ExpandedCard';
import List from '../components/ui/List';
import AlertBar from '../components/ui/AlertBar';
import IconListElement from '../components/ui/IconListElement';
import UserData from '../mixins/UserData';
import Accessibility from '../mixins/Accessibility';
import '../components/icons/headerIcons/career';
import '../components/icons/arrow-right';
import '../components/icons/arrow-left';

const MAXIMUM_SELECTED_CAREER = 1;

export default {
	components: {
		jaHeader: Header,
		jaButton: Button,
		jaExpandedCard: ExpandedCard,
		jaIconListElement: IconListElement,
		jaList: List,
		jaAlertBar: AlertBar
	},
	mixins: [UserData, Accessibility],
	data() {
		return {
			cluster: null,
			selectedCareer: null,
			alertIsVisible: false,
			MAXIMUM_SELECTED_CAREER: MAXIMUM_SELECTED_CAREER,
			expandedCareers: [],
			button: {
				more: 'showMore',
				less: 'showLess'
			}
		};
	},
	watch: {
		userData(data) {
			if (data.studentId) {
				this.getIsEntryVirtualSimulationType();
			}
		}
	},
	created() {
		this.cluster = JSON.parse(sessionStorage.getItem('careerCluster'));
		if (this.userData) {
			this.$store.dispatch('getPauseState');
		}
	},
	mounted() {
		this.$store.commit('setLoading', false);
	},
	methods: {
		careerIsSelected(currCareer) {
			if (currCareer) {
				return (
					!!this.selectedCareer &&
					this.selectedCareer.career_ID == currCareer.career_ID
				);
			} else {
				return false;
			}
		},
		async submitCareer() {
			this.$store.commit('setLoading', true);
			const careerInfo = {
				id: this.userData.studentId,
				career_ID: this.selectedCareer.career_ID
			};

			if (this.isEntryVirtualSimulationType) {
				careerInfo.education_ID = this.selectedCareer.cmsEducation.education_ID;
			}

			const response = await this.$apollo.mutate({
				mutation: gql`
					mutation($update: StudentInputObjectGraphType!) {
						updateIgnoreNullValuesStudent(update: $update) {
							firstName
						}
					}
				`,
				variables: {
					update: careerInfo
				}
			});

			if (response.data) {
				localStorage.removeItem('careerCluster');
				let rerouteTo = this.isEntryVirtualSimulationType
					? 'goals'
					: 'education';
				this.$router.push(`/${rerouteTo}`);
			}
		},
		async getIsEntryVirtualSimulationType() {
			const response = await this.$apollo.query({
				query: gql`
					query isENtryVirtualSimulationType($where: String!) {
						students(where: $where) {
							isEntryVirtualSimulationType
						}
					}
				`,
				variables: {
					where: `id == ${this.userData.studentId}`
				}
			});

			if (response.data) {
				this.isEntryVirtualSimulationType;
			}
		},
		navigateBackwards() {
			sessionStorage.removeItem('careerCluster');
			this.$router.push({
				name: 'clusters',
				params: { selectedCluster: this.cluster.careerCluster_ID }
			});
		},
		toggleCareerSelection(career) {
			if (this.selectedCareer) {
				if (this.selectedCareer.career_ID == career.career_ID) {
					this.alertIsVisible = false;
					this.selectedCareer = null;
				} else {
					this.alertIsVisible = true;
					setTimeout(() => {
						this.announceError(this.$t('careerCountAlert'));
					}, 400);
				}
			} else {
				this.selectedCareer = career;
			}
		},
		toggleCareer(career) {
			let index = this.expandedCareers.indexOf(career);
			if (index === -1) {
				this.expandedCareers.push(career);
			} else {
				this.expandedCareers.splice(index, 1);
			}
		},
		isExpanded(career) {
			return this.expandedCareers.indexOf(career) !== -1;
		}
	}
};
</script>
<style lang="scss" scoped>
.career-select-wrapper {
	content {
		.heading-image {
			background-image: url('../assets/images/career_cluster.png');
		}
		.sub-heading {
			width: 65%;
			margin-left: auto;
			margin-right: auto;
			max-width: 848px;
		}
		.career-select-card-wrapper {
			max-width: 65%;
			margin: auto;

			.expanded-card {
				margin-left: auto;
				margin-right: auto;
				max-width: 848px;
				position: relative;
				display: flex;
				flex-direction: column;

				.heading-3 {
					color: $font-header;
					margin-bottom: 16px;
				}

				.card-description {
					padding-right: 10%;
					display: inline-flex;
					min-width: 100%;
					margin-bottom: 20px;
					color: $font-header;
				}

				.line {
					box-sizing: border-box;
					height: 1px;
					min-width: 404px;
					border: 1px solid #979797;
					opacity: 0.21;
					margin-top: 24px;
					margin-bottom: 24px;
				}

				.btn-show-more {
					display: flex;
					align-items: center;
					color: $font-blue;
					margin-bottom: -10px;
					margin-left: -24px;
					color: $primary-state;
					background-color: transparent;
					justify-content: flex-start;

					@media (min-width: $breakpoint-mid) {
						width: 156px;
					}

					@media (any-hover: hover) {
						&:hover {
							color: $hover-state;
							background-color: transparent;
						}
					}
				}
				.btn-select {
					margin-bottom: -10px;
					margin-left: -35px;
				}

				.expanded-active {
					height: auto;
					color: $font-blue;
					color: $questionnaire-color;
					margin-bottom: 25px;
					margin-left: 3%;
					margin-right: 3%;

					.list-bottom-border {
						border-bottom: 1px solid rgba(151, 151, 151, 0.21);
						border-bottom-width: 1px;
						margin-top: 24px;
						margin-bottom: 24px;
						padding-bottom: 24px;
					}

					.needed-skills-wrapper,
					.outlook-wrapper {
						margin-top: 24px;
						margin-bottom: 24px;
						li {
							display: flex;
							align-items: center;
							list-style: none;
							&::before {
								content: '\2022';
								color: #2cb6f8;
								margin-right: 10px;
							}
						}
					}
				}

				.btn-wrapper {
					display: flex;
					justify-content: space-between;
				}
				p {
					text-align: center;
				}
			}
		}
	}
}

.card-page footer .footer-content {
	display: flex;
	justify-content: space-between;
	width: 100%;
	.btn-back {
		display: flex;
		align-self: left;
		.svg-icon {
			margin-right: 15px;
		}
	}
}
</style>
